import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShieldCheck, Snowflake, Scale, Flame, Recycle, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal } from "../components/store/Reveal";
import { VideoTutorial } from "../components/store/VideoTutorial";
import { Reviews } from "../components/store/Reviews";

const renderIngredients = (text) => {
  if (!text) return null;
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((p, i) =>
    p.startsWith("[") ? (
      <strong key={`${p}-${i}`} className="text-[#2E8A57] font-semibold">{p.slice(1, -1)}</strong>
    ) : (
      <span key={`${p.slice(0, 24)}-${i}`}>{p}</span>
    )
  );
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);
  const [qty, setQty] = useState(1);
  const { addItem, setOpen } = useCart();

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (error) return <div className="max-w-4xl mx-auto px-6 py-24 text-center text-[#78716C]">Prodotto non trovato. <Link to="/prodotti" className="text-[#2E8A57] underline">Torna al menu</Link></div>;
  if (!product) return <div className="max-w-4xl mx-auto px-6 py-24 text-center text-[#78716C]">Caricamento...</div>;

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-10 lg:py-16">
      <Link to="/prodotti" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#2E8A57] transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Torna al menu
      </Link>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <Reveal>
          <div className="rounded-[2rem] overflow-hidden border border-[#E7E0D8] bg-[#F5F0EB] shadow-[0_40px_80px_-40px_rgba(28,25,23,0.35)]">
            <img src={product.image} alt={product.name} className="w-full aspect-square object-cover" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3C6E53] mb-3">{product.category}</p>
            <h1 data-testid="product-detail-name" className="font-serif-display font-bold text-3xl sm:text-4xl tracking-tight text-[#1C1917]">{product.name}</h1>
            <p data-testid="product-detail-price" className="mt-4 font-mono-accent font-semibold text-2xl text-[#2E8A57]">
              {product.price.toFixed(2).replace(".", ",")} €
              {product.original_price && (
                <span data-testid="product-detail-original-price" className="ml-3 text-base text-[#B4ADA3] line-through font-normal">{product.original_price.toFixed(2).replace(".", ",")} €</span>
              )}
              {product.bundle_discount_percent && (
                <span className="ml-2 align-middle rounded-full bg-[#7DC242]/15 text-[#3C6E53] px-3 py-1 text-xs font-bold">−{product.bundle_discount_percent}%</span>
              )}
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#3C6E53]/10 text-[#2D4A3E] px-3.5 py-1.5 text-xs font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" /> Certificato Gluten Free
              </span>
              {product.weight && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F0EB] text-[#44403C] px-3.5 py-1.5 text-xs font-semibold">
                  <Scale className="w-3.5 h-3.5" /> {product.weight}
                </span>
              )}
              {product.shelf_life && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F0EB] text-[#44403C] px-3.5 py-1.5 text-xs font-semibold">
                  <Snowflake className="w-3.5 h-3.5" /> {product.shelf_life}
                </span>
              )}
              <Link to="/packaging" data-testid="detail-eco-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#7DC242]/15 text-[#3C6E53] px-3.5 py-1.5 text-xs font-semibold hover:bg-[#7DC242]/25 transition-colors">
                <Recycle className="w-3.5 h-3.5" /> Packaging riciclabile
              </Link>
            </div>

            {product.bundle_items && product.bundle_items.length > 0 && (
              <div className="mt-8" data-testid="bundle-contents">
                <h2 className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#2E8A57] mb-3">Cosa contiene il box</h2>
                <div className="space-y-2">
                  {product.bundle_items.map((it) => (
                    <Link key={it.slug} to={`/prodotto/${it.slug}`} data-testid={`bundle-content-${it.slug}`} className="flex items-center gap-3 rounded-xl border border-[#E7E0D8] bg-white p-2.5 hover:border-[#2E8A57] transition-colors">
                      <img src={it.image} alt={it.name} className="w-10 h-10 rounded-lg object-cover" />
                      <span className="text-sm font-medium flex-1">{it.name}</span>
                      <span className="font-mono-accent text-xs text-[#78716C] pr-2">{it.price.toFixed(2).replace(".", ",")} €</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {product.ingredients && (
              <div className="mt-8">
                <h2 className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#2E8A57] mb-3">Ingredienti</h2>
                <p data-testid="product-ingredients" className="text-sm text-[#44403C] leading-relaxed">{renderIngredients(product.ingredients)}.</p>
                {product.traces && (
                  <p className="mt-2.5 text-xs text-[#78716C]"><strong className="text-[#2E8A57]">Può contenere tracce di:</strong> {product.traces}.</p>
                )}
              </div>
            )}

            {product.allergens && product.allergens.length > 0 && (
              <div className="mt-6">
                <h2 className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#2E8A57] mb-3">Allergeni</h2>
                <div className="flex flex-wrap gap-2">
                  {product.allergens.map((a) => (
                    <span key={a} className="rounded-full border border-[#2E8A57]/30 text-[#2E8A57] px-3 py-1 text-xs font-medium">{a}</span>
                  ))}
                </div>
              </div>
            )}

            {product.preparation && (
              <div className="mt-6 rounded-2xl bg-[#F5F0EB] p-5">
                <h2 className="font-mono-accent text-xs uppercase tracking-[0.25em] text-[#2E8A57] mb-2 flex items-center gap-2"><Flame className="w-3.5 h-3.5" /> Preparazione</h2>
                <p className="text-sm text-[#44403C] leading-relaxed">{product.preparation}</p>
              </div>
            )}

            <div className="mt-6">
              <VideoTutorial />
            </div>

            <div className="mt-9 flex items-center gap-4">
              <div className="flex items-center gap-3 rounded-full border border-[#E7E0D8] px-4 py-2.5">
                <button data-testid="detail-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="hover:text-[#2E8A57]"><Minus className="w-4 h-4" /></button>
                <span data-testid="detail-qty" className="font-mono-accent w-8 text-center font-semibold">{qty}</span>
                <button data-testid="detail-qty-plus" onClick={() => setQty(qty + 1)} className="hover:text-[#2E8A57]"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                data-testid="detail-add-to-cart"
                onClick={() => {
                  addItem(product, qty);
                  toast.success(`${product.name} × ${qty} aggiunto al carrello`);
                  setOpen(true);
                }}
                className="btn-tuscan flex-1 py-4 rounded-full font-semibold text-sm tracking-wide"
              >
                Aggiungi al carrello — {(product.price * qty).toFixed(2).replace(".", ",")} €
              </button>
            </div>
            <p className="mt-4 text-xs text-[#78716C]">Ordine minimo 100€ · Spedizione refrigerata in tutta Italia · Dati prodotto dal produttore</p>

            <div className="mt-6 flex items-center gap-3">
              <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#78716C]">Condividi il piatto:</span>
              <a
                data-testid="share-whatsapp-btn"
                href={`https://wa.me/?text=${encodeURIComponent(`${product.name} — piatto pronto gluten free 😋 ${window.location.href}`)}`}
                target="_blank"
                rel="noreferrer"
                aria-label="Condividi su WhatsApp"
                className="w-10 h-10 rounded-full border border-[#E7E0D8] flex items-center justify-center text-[#2E8A57] hover:bg-[#2E8A57] hover:text-white hover:border-[#2E8A57] transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
              <button
                data-testid="share-instagram-btn"
                aria-label="Condividi su Instagram"
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.href);
                  toast.success("Link copiato! Incollalo nella tua storia o nei DM di Instagram");
                  window.open("https://www.instagram.com/", "_blank");
                }}
                className="w-10 h-10 rounded-full border border-[#E7E0D8] flex items-center justify-center text-[#2E8A57] hover:bg-[#2E8A57] hover:text-white hover:border-[#2E8A57] transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
      <Reviews slug={slug} />
    </div>
  );
}
